/**
 * Task N — product languages. The whole product is English by default with Chinese as the only alternative.
 * (Task SC, decision 2026-10-06) Bahasa Malaysia is REMOVED as a product language: it can no longer be enabled
 * (ENABLED_LOCALES=…,ms is ignored) and no picker offers it. Old Malay strings may stay in the code (data safety) but
 * no new ones are added. A salon / customer / user that chose "ms" earlier falls back to English.
 */
import type { Locale } from "./types.js";

export const ALL_LOCALES: readonly Locale[] = ["en", "zh", "ms"];
export const DEFAULT_ENABLED_LOCALES: readonly Locale[] = ["en", "zh"];
/** (Task SC) the only languages the product offers; "ms" stays in the Locale type for stored data only */
export const PRODUCT_LOCALES: readonly Locale[] = ["en", "zh"];
export const LOCALE_NAMES: Record<Locale, string> = { en: "English", zh: "中文", ms: "Bahasa Melayu" };
/** short label for language switches (public site) */
export const LOCALE_SHORT: Record<Locale, string> = { en: "EN", zh: "中文", ms: "BM" };

/** Parse the ENABLED_LOCALES flag ("en,zh"); English is always on and first. */
export function parseEnabledLocales(flag: string | null | undefined): Locale[] {
  if (!flag || !flag.trim()) return [...DEFAULT_ENABLED_LOCALES];
  const asked = flag.split(",").map((s) => s.trim().toLowerCase()).filter((s): s is Locale => (PRODUCT_LOCALES as readonly string[]).includes(s));
  return ["en", ...PRODUCT_LOCALES.filter((l) => l !== "en" && asked.includes(l))];
}

let enabled: Locale[] = [...DEFAULT_ENABLED_LOCALES];
/** Set once at start-up (API: from the env flag; web: from /api/public/platform). */
export function setEnabledLocales(list: readonly Locale[] | string | null | undefined) {
  enabled = typeof list === "string" || list == null ? parseEnabledLocales(list as string | null | undefined) : parseEnabledLocales(list.join(","));
}
export const enabledLocales = (): Locale[] => [...enabled];
export const isLocaleEnabled = (l: string | null | undefined): l is Locale => !!l && (enabled as string[]).includes(l);
/** Normalise any language tag (en-MY, zh-Hans, ms …) to an enabled product language; disabled/unknown → English. */
export function effectiveLocale(l: string | null | undefined): Locale {
  const v = (l ?? "").toLowerCase();
  const base: Locale | null = v.startsWith("zh") ? "zh" : v.startsWith("ms") ? "ms" : v.startsWith("en") ? "en" : null;
  return base && isLocaleEnabled(base) ? base : "en";
}
/** Keep only enabled languages of a list (order kept); never empty. */
export function enabledOnly<L extends string>(list: readonly L[]): L[] {
  const out = list.filter((l) => isLocaleEnabled(l));
  return out.length ? out : (["en"] as L[]);
}
